const idOf = (value) => String(value?._id || value || '');

/**
 * `posts/getById` returns every comment on a post as one flat list, replies included, linked by
 * `replyTo`. This groups them into threads: each top-level comment (keeping the order it arrived
 * in, newest first) with all of its descendants flattened beneath it, oldest first, so a thread
 * reads top to bottom as a conversation.
 *
 * A reply whose parent is not in the list (deleted, or not fetched) is shown as its own thread
 * rather than dropped.
 */
export function buildCommentThreads(comments = []) {
  const byId = new Map(comments.map((x) => [idOf(x._id), x]));

  const rootIdOf = (comment) => {
    let current = comment;

    // The guard stops a malformed cycle from looping forever.
    for (let hops = 0; hops < 100; hops++) {
      const parentId = idOf(current.replyTo);
      if (!parentId || !byId.has(parentId)) return idOf(current._id);
      current = byId.get(parentId);
    }

    return idOf(current._id);
  };

  const repliesByRoot = new Map();

  comments.forEach((comment) => {
    const rootId = rootIdOf(comment);
    if (rootId === idOf(comment._id)) return;

    repliesByRoot.set(rootId, [...(repliesByRoot.get(rootId) || []), comment]);
  });

  return comments
    .filter((comment) => rootIdOf(comment) === idOf(comment._id))
    .map((comment) => ({
      ...comment,
      replies: (repliesByRoot.get(idOf(comment._id)) || []).sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      )
    }));
}

export default buildCommentThreads;
